import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../services/api';
import { AdPosition } from '../../types';
import { trackPredConv } from '../../utils/analytics';
import './Ad.css';

interface AdProps {
  positionName: string;
  className?: string;
  style?: React.CSSProperties;
}

const Ad: React.FC<AdProps> = ({ positionName, className = '', style = {} }) => {
  const [adPosition, setAdPosition] = useState<AdPosition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const adContainerRef = useRef<HTMLDivElement>(null);
  const adInitializedRef = useRef<boolean>(false);
  const displayPredPointerRef = useRef<boolean>(false);

  const loadAdPosition = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getAdPositionByName(positionName);
      if (response.code === 0 && response.data.is_active && response.data.code) {
        setAdPosition(response.data);
      } else if (response.code === 0 && response.data.is_active) {
        // 即便没有可视广告代码，如果启用也可能需要 header_code
        setAdPosition(response.data);
      }
    } catch (error) {
      console.error(`Failed to load ad position: ${positionName}`, error);
      setError('Failed to load ad');
    } finally {
      setLoading(false);
    }
  }, [positionName]);

  useEffect(() => {
    loadAdPosition();
  }, [loadAdPosition]);

  useEffect(() => {
    if (!adPosition || !adPosition.is_active || !adPosition.code) {
      return;
    }

    const adContainer = adContainerRef.current;
    if (!adContainer) {
      return;
    }

    let pointerTimer: number | undefined;
    const focusCheckTimers: number[] = [];
    const pointerOptions: AddEventListenerOptions = { capture: true, passive: true };

    const findFocusedAdIframe = () => {
      const activeElement = document.activeElement;
      if (!activeElement || activeElement.tagName !== 'IFRAME') {
        return null;
      }

      return adContainer.contains(activeElement) ? activeElement : null;
    };

    const trackIframeFocus = () => {
      if (!findFocusedAdIframe()) {
        return;
      }

      trackPredConv({
        ad_type: 'display',
        signal_type: displayPredPointerRef.current ? 'pointer_then_iframe_pred' : 'iframe_pred',
        ad_position: positionName,
      });
      displayPredPointerRef.current = false;
    };

    const scheduleFocusChecks = () => {
      [100, 300, 600].forEach((delay) => {
        focusCheckTimers.push(window.setTimeout(trackIframeFocus, delay));
      });
    };

    const handlePointerSignal = () => {
      displayPredPointerRef.current = true;
      if (pointerTimer) {
        window.clearTimeout(pointerTimer);
      }
      pointerTimer = window.setTimeout(() => {
        displayPredPointerRef.current = false;
      }, 800);
      scheduleFocusChecks();
    };

    const handleWindowBlur = () => {
      window.setTimeout(trackIframeFocus, 100);
    };

    adContainer.addEventListener('pointerdown', handlePointerSignal, pointerOptions);
    adContainer.addEventListener('touchstart', handlePointerSignal, pointerOptions);
    adContainer.addEventListener('mousedown', handlePointerSignal, pointerOptions);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('pagehide', trackIframeFocus);
    document.addEventListener('visibilitychange', trackIframeFocus);

    return () => {
      if (pointerTimer) {
        window.clearTimeout(pointerTimer);
      }
      focusCheckTimers.forEach((timer) => window.clearTimeout(timer));
      adContainer.removeEventListener('pointerdown', handlePointerSignal, pointerOptions);
      adContainer.removeEventListener('touchstart', handlePointerSignal, pointerOptions);
      adContainer.removeEventListener('mousedown', handlePointerSignal, pointerOptions);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('pagehide', trackIframeFocus);
      document.removeEventListener('visibilitychange', trackIframeFocus);
    };
  }, [adPosition, positionName]);

  // 追加 header_code 到 <head>（直接注入元素，按广告位名称去重，并在卸载时清理）
  useEffect(() => {
    if (!adPosition || !adPosition.header_code) {
      return;
    }

    const markerAttr = 'data-ad-header';
    const markerValue = positionName;

    // 如果该广告位已注入过，则跳过（检查 data-position-name）
    if (document.head.querySelector(`[data-position-name="${markerValue}"]`)) {
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = adPosition.header_code.trim();

    const insertedElements: Element[] = [];
    const allowedInHead = new Set(['SCRIPT', 'LINK', 'META', 'STYLE', 'NOSCRIPT', 'TITLE', 'BASE']);

    Array.from(template.content.childNodes).forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const el = node as HTMLElement;
      const tag = el.tagName.toUpperCase();
      if (!allowedInHead.has(tag)) {
        return;
      }

      if (tag === 'SCRIPT') {
        const s = document.createElement('script');
        // 复制属性（但跳过 data-ad-header，因为 AdSense 不支持）
        Array.from(el.attributes).forEach((attr) => {
          if (attr.name !== markerAttr) {
            s.setAttribute(attr.name, attr.value);
          }
        });
        // 使用 data-position-name 作为标记（不影响 AdSense）
        s.setAttribute('data-position-name', markerValue);
        // 复制内联脚本内容
        s.text = el.textContent || '';
        document.head.appendChild(s);
        insertedElements.push(s);
      } else {
        const cloned = el.cloneNode(true) as Element;
        // 移除 data-ad-header，使用 data-position-name 代替
        cloned.removeAttribute(markerAttr);
        cloned.setAttribute('data-position-name', markerValue);
        document.head.appendChild(cloned);
        insertedElements.push(cloned);
      }
    });

    return () => {
      insertedElements.forEach((el) => {
        if (el.parentElement === document.head) {
          document.head.removeChild(el);
        }
      });
    };
  }, [adPosition, positionName]);

  // 初始化 AdSense 广告
  useEffect(() => {
    if (!adPosition || !adPosition.is_active || !adPosition.code) {
      adInitializedRef.current = false;
      return;
    }

    // 如果已经初始化过，检查是否需要重新初始化（广告代码可能已更新）
    if (adInitializedRef.current) {
      const adContainer = adContainerRef.current;
      if (adContainer) {
        const adElements = adContainer.querySelectorAll('.adsbygoogle:not([data-adsbygoogle-status])');
        // 如果有未初始化的广告元素，重置状态
        if (adElements.length > 0) {
          adInitializedRef.current = false;
        } else {
          // 已经初始化，不需要重复
          return;
        }
      } else {
        return;
      }
    }

    // 等待 AdSense 脚本加载完成并初始化广告
    const initAdSense = () => {
      try {
        // 检查 adsbygoogle 是否已定义
        if (typeof window === 'undefined' || !(window as any).adsbygoogle) {
          return;
        }

        // 使用 ref 获取当前广告容器
        const adContainer = adContainerRef.current;
        if (!adContainer) {
          return;
        }

        // 检查容器是否有宽度。高度可以由 AdSense 在初始化后自动决定。
        const rect = adContainer.getBoundingClientRect();
        if (rect.width === 0) {
          // 容器还没有可用宽度，稍后重试
          return;
        }

        // 查找当前广告容器中未初始化的 adsbygoogle 元素
        const adElements = adContainer.querySelectorAll('.adsbygoogle:not([data-adsbygoogle-status])');
        if (adElements.length > 0) {
          // 为每个未初始化的广告元素调用 push
          adElements.forEach((element) => {
            try {
              ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
              adInitializedRef.current = true;
            } catch (err) {
              console.error('Error pushing ad:', err);
            }
          });
        }
      } catch (error) {
        console.error('Failed to initialize AdSense:', error);
      }
    };

    // 延迟执行，确保 DOM 已完全渲染并且容器有可用宽度
    const tryInit = () => {
      const adContainer = adContainerRef.current;
      if (adContainer) {
        const rect = adContainer.getBoundingClientRect();
        if (rect.width > 0) {
          // 容器有可用宽度，可以初始化
          if (typeof window !== 'undefined' && (window as any).adsbygoogle) {
            initAdSense();
          } else {
            // 等待 adsbygoogle 脚本加载
            const checkInterval = setInterval(() => {
              if (typeof window !== 'undefined' && (window as any).adsbygoogle) {
                clearInterval(checkInterval);
                initAdSense();
              }
            }, 100);
            setTimeout(() => clearInterval(checkInterval), 10000);
          }
        } else {
          // 容器还没有可用宽度，稍后重试
          setTimeout(tryInit, 100);
        }
      }
    };

    // 初始延迟，确保 React 已完成渲染
    const timeoutId = setTimeout(tryInit, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [adPosition]);

  // 如果广告位不存在、未启用或没有代码，则不显示
  if (loading || !adPosition || !adPosition.is_active || !adPosition.code) {
    return null;
  }

  if (error) {
    return null;
  }

  // 处理广告代码，移除 script 标签（因为 dangerouslySetInnerHTML 不会执行 script）
  const processAdCode = (code: string): string => {
    // 移除 script 标签及其内容，因为我们会手动初始化
    return code.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  };

  return (
    <div className={`ad-frame ${className}`} style={style}>
      <div className="ad-slot">
        <div
          ref={adContainerRef}
          className="ad-container"
        >
          <div
            className="ad-content"
            dangerouslySetInnerHTML={{ __html: processAdCode(adPosition.code) }}
          />
        </div>
      </div>
    </div>
  );
};

export default Ad;
